import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {PopoverDefaultAlignment} from '@Components/popover/popover.types';
import {DateTimeSelector} from '@Components/date-time-selector/date-time-selector';
import {Button, IconPlacement, ButtonType, ButtonVariant, ButtonSize} from '@Components/button';
import {Text, TextSize} from '@Components/text';
import {ToggleSwitch, ToggleSwitchV2Size} from '@Components/toggle-switch-v2/toggle-switch';
import {ToggleSwitchStyleVariant} from '@Components/toggle-switch-v2/toggle-switch-v2.types';
import {
  BestTimeCalculationState,
  formatOptimalSendTime,
  getMinAllowedScheduleDate,
  getScheduleDateChangeEffect,
  isBestTimeResolving,
  ScheduleDateChangeEffect,
} from '@Libraries/best-send-time.library';
import {assertNever} from '@Utils/assert-never.util';
import type {ScheduleFormProps} from './schedule-form.types';
import {DEFAULT_SCHEDULE_TIME_DROPDOWN_ID} from './schedule-form.types';
import {clampToMinAllowedDate, getScheduleDateToSave} from './schedule-form.library';
import styles from './schedule-form.module.scss';

export function ScheduleForm({
  initialDate,
  isEditingExistingSchedule,
  showInlineTitle = true,
  timeDropdownId = DEFAULT_SCHEDULE_TIME_DROPDOWN_ID,
  onSave,
  onRemove,
  bestTime,
}: ScheduleFormProps): ReactElement {
  const [minAllowedDate] = useState<Date>(getMinAllowedScheduleDate);
  const [dateTime, setDateTime] = useState<Date>(() => clampToMinAllowedDate(initialDate, minAllowedDate));
  const [hasDisallowedScheduleTime, setHasDisallowedScheduleTime] = useState<boolean>(false);
  const [hasUserChosenDateTime, setHasUserChosenDateTime] = useState<boolean>(false);
  const hasBestTimeFeature = bestTime !== undefined;
  const effectiveBestTimeState = bestTime?.state ?? BestTimeCalculationState.OFF;

  useEffect(() => {
    if (effectiveBestTimeState === BestTimeCalculationState.DONE && bestTime?.optimalSendTime) {
      setDateTime(bestTime?.optimalSendTime);
    }
  }, [effectiveBestTimeState, bestTime?.optimalSendTime]);

  /**
   * Toggling off mid-calculation cancels it, so a best-time request that never settles can
   * never strand the form with its save button disabled.
   */
  const handleToggleBestTime = (): void => {
    setHasDisallowedScheduleTime(false);

    if (effectiveBestTimeState === BestTimeCalculationState.DONE || isBestTimeResolving(effectiveBestTimeState)) {
      bestTime?.onDisable();

      return;
    }

    bestTime?.onEnable(dateTime);
  };

  const handleDateTimeChange = (next: Date): void => {
    const effect = getScheduleDateChangeEffect(next, dateTime, effectiveBestTimeState);

    setHasDisallowedScheduleTime(false);
    setHasUserChosenDateTime(true);
    setDateTime(next);

    switch (effect) {
      case ScheduleDateChangeEffect.RECOMPUTE_BEST_TIME:
        bestTime?.onEnable(next);
        return;
      case ScheduleDateChangeEffect.DISABLE_BEST_TIME:
        bestTime?.onDisable();
        return;
      case ScheduleDateChangeEffect.NONE:
        return;
      default:
        assertNever(effect);
    }
  };

  const handleSave = (): void => {
    const optimalSendTime = bestTime?.optimalSendTime ?? null;
    const isBestTimeEnabled = effectiveBestTimeState === BestTimeCalculationState.DONE && optimalSendTime !== null;
    const freshMinAllowedDate = getMinAllowedScheduleDate();
    const scheduleAt = getScheduleDateToSave({
      isBestTimeEnabled,
      optimalSendTime,
      hasUserChosenDateTime,
      currentDateTime: dateTime,
      minAllowedDate: freshMinAllowedDate,
    });

    if (!isBestTimeEnabled && scheduleAt.getTime() < freshMinAllowedDate.getTime()) {
      setHasDisallowedScheduleTime(true);
      return;
    }

    onSave(scheduleAt, isBestTimeEnabled);
  };

  const renderBestTimeSubtext = (): ReactElement => {
    if (effectiveBestTimeState === BestTimeCalculationState.DONE && bestTime?.optimalSendTime) {
      return <Text size={TextSize.SM} val={`${window.i18next.t('pmwjs_all_emails_will_be_sent_by')} ${formatOptimalSendTime(bestTime?.optimalSendTime)}`} className="content-ai" />;
    }

    if (effectiveBestTimeState === BestTimeCalculationState.ERROR) {
      return <Text size={TextSize.SM} val={window.i18next.t('pmwjs_best_time_passed_pick_another_slot')} className="content-sub-text" dangerouslySetInnerHTML />;
    }

    return <Text size={TextSize.SM} val={window.i18next.t('pmwjs_send_at_best_time_description')} className="content-sub-text" />;
  };

  const renderBestTimeDescription = (): ReactElement => {
    const titleClassName = effectiveBestTimeState === BestTimeCalculationState.ERROR ? 'content-body' : 'ai-text-dark';

    return (
      <div className={styles.bestTimeText}>
        <Text size={TextSize.MD} bold val={window.i18next.t('pmwjs_send_best_time')} className={titleClassName} />
        {renderBestTimeSubtext()}
      </div>
    );
  };

  const renderFooterAction = (): ReactElement => {
    const isSaveBlocked = isBestTimeResolving(effectiveBestTimeState);

    if (isEditingExistingSchedule) {
      return (
        <div className={styles.actionButtonsRow}>
          <Button
            type={ButtonType.SECONDARY} variant={ButtonVariant.ERROR}
            size={ButtonSize.MEDIUM}
            icon="icon-delete"
            iconPlacement={IconPlacement.LEFT}
            text={window.i18next.t('pmwjs_remove')}
            onClick={onRemove}
            customClasses={styles.actionButton}
          />
          <Button type={ButtonType.PRIMARY} size={ButtonSize.MEDIUM} text={window.i18next.t('pmwjs_update')} onClick={handleSave} disabled={isSaveBlocked} customClasses={styles.actionButton} />
        </div>
      );
    }

    return <Button type={ButtonType.PRIMARY} size={ButtonSize.MEDIUM} text={window.i18next.t('pmwjs_done')} onClick={handleSave} disabled={isSaveBlocked} isFullWidth />;
  };

  const renderInlineTitle = (): ReactElement | null => {
    if (!showInlineTitle) {
      return null;
    }

    return <Text size={TextSize.MD} bold val={window.i18next.t('pmwjs_select_date_and_time')} />;
  };

  const renderDisallowedScheduleTimeError = (): ReactElement | null => {
    if (!hasDisallowedScheduleTime) {
      return null;
    }

    return <Text size={TextSize.SM} val={window.i18next.t('pmwjs_disallowed_scheduling')} className={styles.errorMessage} />;
  };

  const renderDateTimeFields = (): ReactElement => {
    return (
      <div className={styles.fieldsSection}>
        {renderInlineTitle()}
        <DateTimeSelector
          value={dateTime}
          onChange={handleDateTimeChange}
          minDate={minAllowedDate}
          timeDropdownId={timeDropdownId}
          calendarAlignment={PopoverDefaultAlignment.TOP_CENTER}
          showAiHighlight={effectiveBestTimeState === BestTimeCalculationState.DONE}
        />
        {renderDisallowedScheduleTimeError()}
      </div>
    );
  };

  const renderBestTimeRow = (): ReactElement | null => {
    if (!hasBestTimeFeature) {
      return null;
    }

    return (
      <div className={styles.bestTimeRow}>
        {renderBestTimeDescription()}
        <ToggleSwitch
          variant={ToggleSwitchStyleVariant.AI}
          isChecked={effectiveBestTimeState === BestTimeCalculationState.DONE}
          isLoading={effectiveBestTimeState === BestTimeCalculationState.CALCULATING}
          onChange={handleToggleBestTime}
          size={ToggleSwitchV2Size.SMALL}
        />
      </div>
    );
  };

  const renderFooterSection = (): ReactElement => {
    return (
      <div className={styles.footerSection}>
        {renderBestTimeRow()}
        {renderFooterAction()}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {renderDateTimeFields()}
      {renderFooterSection()}
    </div>
  );
}
